import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { type SyntheticEvent, type ReactElement, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Notice } from './login-view';

/** Button type that submits the form; a constant because the i18n lint rule flags string attributes. */
const SUBMIT = 'submit';

/** Props of {@link PasswordStep}. */
export interface PasswordStepProps {
  /** The failure to show, used here only to put the cursor back in the empty field. */
  readonly notice: Notice | null;
  readonly pending: boolean;
  /** True after `TELEGRAM_LOGIN_NOT_STARTED`: the login must restart with a new code. */
  readonly showNewCode: boolean;
  /** The password, passed once; the field is emptied right after (R19). */
  readonly onSubmit: (password: string) => void;
  readonly onNewCode: () => void;
}

/**
 * Telegram password step of E2 (specs/S4-telegram-login/spec.md R16, R19): why it is needed, where
 * the trader chose it, that no one can read it. `type="password"` and `autoComplete="off"` so no
 * browser or password manager offers to save it (Proposal P8, I9).
 */
export function PasswordStep({
  notice,
  pending,
  showNewCode,
  onSubmit,
  onNewCode,
}: PasswordStepProps): ReactElement {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const field = useRef<HTMLInputElement>(null);
  useEffect(() => {
    field.current?.focus();
  }, [notice]);

  function submit(event: SyntheticEvent): void {
    event.preventDefault();
    if (pending || value === '') return;
    const password = value;
    // R19, I9: the password leaves the field the moment it is handed over, success or not.
    setValue('');
    onSubmit(password);
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box>
        <Typography sx={{ fontSize: 13, color: 'success.main', fontWeight: 600 }}>
          {t('onboarding.telegram.password.accepted')}
        </Typography>
        <Typography
          component="h1"
          sx={{ fontSize: 26, lineHeight: 1.2, letterSpacing: '-0.02em', fontWeight: 700 }}
        >
          {t('onboarding.telegram.password.title')}
        </Typography>
      </Box>
      <Typography sx={{ lineHeight: 1.5 }}>{t('onboarding.telegram.password.why')}</Typography>
      <Box
        component="form"
        noValidate
        onSubmit={submit}
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <TextField
          label={t('onboarding.telegram.password.label')}
          helperText={t('onboarding.telegram.password.help')}
          type="password"
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
          }}
          inputRef={field}
          fullWidth
          slotProps={{ htmlInput: { autoComplete: 'off' } }}
        />
        <Box
          sx={(theme) => ({
            bgcolor: theme.fx.infoBg,
            borderRadius: 2,
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          })}
        >
          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
            {t('onboarding.telegram.password.privacyTitle')}
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.45 }}>
            {t('onboarding.telegram.password.privacy')}
          </Typography>
        </Box>
        <Button
          type={SUBMIT}
          variant="contained"
          size="large"
          fullWidth
          disabled={pending || value === ''}
          sx={{ minHeight: 52 }}
        >
          {t('onboarding.telegram.password.confirm')}
        </Button>
        {showNewCode ? (
          <Button variant="text" onClick={onNewCode} disabled={pending} sx={{ minHeight: 44 }}>
            {t('onboarding.telegram.code.newCode')}
          </Button>
        ) : null}
      </Box>
    </Box>
  );
}
